import type { ReactNode, CSSProperties } from 'react';
import { Icon } from './Icon.js';

// Shared presentational primitives matching the dashboard design system.

export function PageHead({ title, sub, actions }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="pagehead">
      <div>
        <h1 className="pagehead__h">{title}</h1>
        {sub && <p className="pagehead__sub">{sub}</p>}
      </div>
      {actions && <div className="pagehead__actions">{actions}</div>}
    </div>
  );
}

type BadgeKind = 'ok' | 'warn' | 'accent' | 'muted' | 'danger';
export function Badge({ kind = 'muted', children, plain, icon }: { kind?: BadgeKind; children: ReactNode; plain?: boolean; icon?: string }) {
  return (
    <span className={`badge badge--${kind} ${plain ? 'badge--plain' : ''}`}>
      {icon && <Icon name={icon} />}
      {children}
    </span>
  );
}

export function Avatar({ initial, size = 38, grad = true }: { initial: ReactNode; size?: number; grad?: boolean }) {
  const style: CSSProperties = {
    width: size, height: size, fontSize: size * 0.4,
    background: grad ? undefined : 'var(--bg-4)',
    color: grad ? '#fff' : 'var(--ink-2)',
  };
  return <div className={`avatar ${grad ? 'avatar--grad' : ''}`} style={style}>{initial}</div>;
}

export function Stat({ icon, label, value, unit, delta, deltaDir }: { icon: string; label: ReactNode; value: ReactNode; unit?: ReactNode; delta?: ReactNode; deltaDir?: 'up' | 'down' }) {
  return (
    <div className="stat">
      <div className="stat__top">
        <div className="stat__icon"><Icon name={icon} /></div>
        <span>{label}</span>
      </div>
      <div className="stat__n">{value}{unit && <span className="unit">{unit}</span>}</div>
      {delta && (
        <div className={`stat__delta stat__delta--${deltaDir === 'down' ? 'down' : 'up'}`}>
          <Icon name={deltaDir === 'down' ? 'arrowdown' : 'arrowup'} />{delta}
        </div>
      )}
    </div>
  );
}

export function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <button type="button" className={`switch ${on ? 'switch--on' : ''}`} onClick={onClick} aria-pressed={on} />;
}

export function Channel({ ch }: { ch: 'ig' | 'fb' | 'wa' | string }) {
  const map: Record<string, string> = { ig: 'إنستغرام', fb: 'ماسنجر', wa: 'واتساب', instagram: 'إنستغرام', facebook: 'ماسنجر', whatsapp: 'واتساب' };
  const key = ch === 'instagram' ? 'ig' : ch === 'facebook' ? 'fb' : ch === 'whatsapp' ? 'wa' : ch;
  return <span className="chan"><span className={`chan__d chan__d--${key}`} />{map[ch] ?? ch}</span>;
}

export function EmptyState({ icon, h, p, action }: { icon: string; h: ReactNode; p?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div>
        <div className="empty__icon"><Icon name={icon} /></div>
        <div className="empty__h">{h}</div>
        {p && <div className="empty__p">{p}</div>}
        {action && <div className="mt16">{action}</div>}
      </div>
    </div>
  );
}

// Convert Latin digits to Arabic-Indic for display.
export function toAr(v: string | number): string {
  return String(v).replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[+d] ?? d);
}
